import { useEffect, useRef, useState } from 'react';
import { STRINGS } from '@/strings/strings';
import { HintBox } from '../preview/HintBox';

/**
 * Screen-2 state 7 — `anchor_not_in_review` overlay.
 *
 * v3.1 §6 line 293 verbatim: "leere Fläche, Hinweiskasten mittig in
 * Navy, kurzer Balken, danach automatische Navigation."
 *
 * Sebastian ruling 2026-08-05 §4: refetch the envelope and let the
 * step-mapper route from the fresh status (G12 — status is the only
 * source). Never hardcode the target route. Timing: fire the refetch
 * immediately, hold the bar for at least 800 ms so it does not flash,
 * then hand back control. If the refetch is slower, the bar runs as
 * long as it runs.
 *
 * ═══ HOW THE "AUTO-NAV" IS IMPLICIT ══════════════════════════════════
 *
 * BookShell owns the envelope fetch. When `refetch()` resolves with a
 * new status, `BookRoute` re-renders and dispatches to whatever screen
 * the new status maps to. No `navigate(...)` call needed here — the
 * shell chrome + outlet + status-to-step wiring already IS the router.
 *
 * ═══ 800 ms FLOOR (Sebastian ruling §4) ══════════════════════════════
 *
 * `minDelayPassed` toggles true after 800 ms. The overlay stays
 * mounted until (a) 800 ms have elapsed AND (b) the envelope has
 * updated to a status that BookRoute dispatches away from
 * `anchor_review`. Both conditions live upstream (BookRoute reads
 * the fresh envelope; this component just holds the bar visible
 * until the floor lifts).
 *
 * The progress bar animates via a CSS transition on `width` from 30%
 * to 100% across 800 ms — not tied to actual progress (there is no
 * progress signal), just a visual anchor for the wait.
 */

const MIN_DELAY_MS = 800;

export interface AnchorNotInReviewOverlayProps {
  /** Refetch the envelope so BookRoute can re-dispatch. */
  readonly onRefetch: () => void;
}

export function AnchorNotInReviewOverlay({ onRefetch }: AnchorNotInReviewOverlayProps) {
  const didRefetch = useRef(false);
  const [minDelayPassed, setMinDelayPassed] = useState(false);

  useEffect(() => {
    if (!didRefetch.current) {
      didRefetch.current = true;
      onRefetch();
    }
    const timer = setTimeout(() => setMinDelayPassed(true), MIN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [onRefetch]);

  return (
    <div className="flex flex-col items-center pt-8 pb-4">
      <HintBox className="max-w-[520px]">
        <p className="text-base text-dream-navy leading-relaxed text-center">
          {STRINGS.book.anchor.notInReviewBody}
        </p>
      </HintBox>
      <div
        className="mt-6 h-2 w-full max-w-xs overflow-hidden rounded-full bg-dream-navy/15"
        role="progressbar"
        aria-busy="true"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={STRINGS.book.anchor.notInReviewBody}
      >
        <div
          className="h-full bg-dream-gold transition-[width] ease-linear"
          style={{
            width: minDelayPassed ? '100%' : '30%',
            transitionDuration: `${MIN_DELAY_MS}ms`,
          }}
        />
      </div>
    </div>
  );
}
